"use client";

import { useState } from "react";
import { useToolNumber, useToolUi } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import { parseIntegerInput } from "@/lib/parse-number";

type Op = "add" | "sub" | "mul" | "div";

function gcd(a: number, b: number): number {
  let x = Math.abs(a);
  let y = Math.abs(b);
  while (y !== 0) {
    const t = y;
    y = x % y;
    x = t;
  }
  return x;
}

function lcm(a: number, b: number): number {
  return (a / gcd(a, b)) * b;
}

interface Frac {
  num: number;
  den: number;
}

interface Worked {
  num: number;
  den: number;
  steps: string[];
}

type Strings = { [K in keyof typeof FRACTION_CALCULATOR_STRINGS]: string };

function fracStr(f: Frac): string {
  return `${f.num}/${f.den}`;
}

function addOrSubtract(f1: Frac, f2: Frac, op: Op, strings: Strings): Worked {
  const common = lcm(f1.den, f2.den);
  const m1 = common / f1.den;
  const m2 = common / f2.den;
  const n1 = f1.num * m1;
  const n2 = f2.num * m2;
  const symbol = op === "add" ? "+" : "−";
  const num = op === "add" ? n1 + n2 : n1 - n2;
  return {
    num,
    den: common,
    steps: [
      t(strings.stepCommonDenominator, {
        den1: f1.den,
        den2: f2.den,
        common,
      }),
      `${fracStr(f1)} = (${f1.num} × ${m1})/${common} = ${n1}/${common}`,
      `${fracStr(f2)} = (${f2.num} × ${m2})/${common} = ${n2}/${common}`,
      `${n1}/${common} ${symbol} ${n2}/${common} = ${num}/${common}`,
    ],
  };
}

function multiply(f1: Frac, f2: Frac, strings: Strings): Worked {
  return {
    num: f1.num * f2.num,
    den: f1.den * f2.den,
    steps: [
      strings.stepMultiplyStraightAcross,
      `${fracStr(f1)} × ${fracStr(f2)} = (${f1.num} × ${f2.num})/(${f1.den} × ${f2.den}) = ${f1.num * f2.num}/${f1.den * f2.den}`,
    ],
  };
}

function divide(f1: Frac, f2: Frac, strings: Strings): Worked {
  // Flipping a negative fraction moves its sign to the top: 1/2 ÷ (−3/4) inverts to −4/3.
  const inverted = normalizeSign({ num: f2.den, den: f2.num });
  return {
    num: f1.num * inverted.num,
    den: f1.den * inverted.den,
    steps: [
      t(strings.stepInvertAndMultiply, {
        first: fracStr(f1),
        second: fracStr(f2),
        inverted: fracStr(inverted),
      }),
      `(${f1.num} × ${inverted.num})/(${f1.den} × ${inverted.den}) = ${f1.num * inverted.num}/${f1.den * inverted.den}`,
    ],
  };
}

export function compute(
  f1: Frac,
  f2: Frac,
  op: Op,
  strings: Strings = FRACTION_CALCULATOR_STRINGS
): Worked {
  if (op === "add" || op === "sub") {
    return addOrSubtract(f1, f2, op, strings);
  }
  if (op === "mul") {
    return multiply(f1, f2, strings);
  }
  return divide(f1, f2, strings);
}

export function normalizeSign(f: Frac): Frac {
  if (f.den < 0) {
    return { num: -f.num, den: -f.den };
  }
  return f;
}

export function simplify(
  raw: Frac,
  strings: Strings = FRACTION_CALCULATOR_STRINGS
): Worked {
  const f = normalizeSign(raw);
  const g = gcd(f.num, f.den);
  if (g <= 1) {
    return {
      num: f.num,
      den: f.den,
      steps: [t(strings.stepAlreadyLowestTerms, { fraction: fracStr(f) })],
    };
  }
  return {
    num: f.num / g,
    den: f.den / g,
    steps: [
      t(strings.stepGcd, { num: Math.abs(f.num), den: f.den, g }),
      `(${f.num} ÷ ${g})/(${f.den} ÷ ${g}) = ${f.num / g}/${f.den / g}`,
    ],
  };
}

function mixedString(f: Frac): string {
  const abs = Math.abs(f.num);
  const whole = Math.floor(abs / f.den);
  const rest = abs % f.den;
  const sign = f.num < 0 ? "−" : "";
  if (rest === 0) {
    return `${sign}${whole}`;
  }
  if (whole === 0) {
    return `${sign}${rest}/${f.den}`;
  }
  return `${sign}${whole} ${rest}/${f.den}`;
}

function decimalString(
  f: Frac,
  formatNum: (value: number, options?: Intl.NumberFormatOptions) => string
): string {
  const rounded = Math.round((f.num / f.den) * 1_000_000) / 1_000_000;
  return formatNum(rounded, { maximumFractionDigits: 6 });
}

function FractionField({
  idPrefix,
  label,
  num,
  den,
  onNum,
  onDen,
  strings,
}: {
  idPrefix: string;
  label: string;
  num: string;
  den: string;
  onNum: (value: string) => void;
  onDen: (value: string) => void;
  strings: typeof FRACTION_CALCULATOR_STRINGS;
}) {
  return (
    <div className="space-y-2">
      <p className="font-medium text-foreground text-sm">{label}</p>
      <div className="flex flex-col items-center gap-1">
        <label className="sr-only" htmlFor={`${idPrefix}-num`}>
          {t(strings.numeratorLabel, { label })}
        </label>
        <input
          className="w-24 rounded-lg border border-border bg-card px-3 py-2 text-center text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary"
          id={`${idPrefix}-num`}
          onChange={(e) => onNum(e.target.value)}
          type="number"
          value={num}
        />
        <div aria-hidden="true" className="h-px w-24 bg-border" />
        <label className="sr-only" htmlFor={`${idPrefix}-den`}>
          {t(strings.denominatorLabel, { label })}
        </label>
        <input
          className="w-24 rounded-lg border border-border bg-card px-3 py-2 text-center text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary"
          id={`${idPrefix}-den`}
          onChange={(e) => onDen(e.target.value)}
          type="number"
          value={den}
        />
      </div>
    </div>
  );
}

function validationMessage(
  f1: Frac | null,
  f2: Frac | null,
  op: Op,
  strings: typeof FRACTION_CALCULATOR_STRINGS
): string | null {
  if (f1 === null || f2 === null) {
    return strings.enterWholeNumbersError;
  }
  if (op === "div" && f2.num === 0) {
    return strings.divideByZeroFractionError;
  }
  return null;
}

export function parseFraction(num: string, den: string): Frac | null {
  const n = parseIntegerInput(num);
  const d = parseIntegerInput(den);
  if (n === null || d === null || d === 0) {
    return null;
  }
  // The sign always lives on the numerator, so the working never shows 3/−4.
  return normalizeSign({ num: n, den: d });
}

/** English strings. A translated content file overrides these through `ui`. */
export const FRACTION_CALCULATOR_STRINGS = {
  add: "Add",
  subtract: "Subtract",
  multiply: "Multiply",
  divide: "Divide",
  twoFractions: "Two fractions",
  firstFraction: "First fraction",
  secondFraction: "Second fraction",
  numeratorLabel: "{label} numerator",
  denominatorLabel: "{label} denominator",
  operation: "Operation",
  enterWholeNumbersError:
    "Enter whole numbers in every box. Denominators cannot be zero.",
  divideByZeroFractionError:
    "Cannot divide by a fraction with a zero numerator (that would be dividing by zero).",
  improperFraction: "Improper fraction",
  mixedNumber: "Mixed number",
  decimal: "Decimal",
  stepCommonDenominator: "Common denominator: LCM({den1}, {den2}) = {common}",
  stepMultiplyStraightAcross:
    "Multiply straight across: numerator × numerator, denominator × denominator",
  stepInvertAndMultiply:
    "Invert the second fraction and multiply: {first} ÷ {second} = {first} × {inverted}",
  stepAlreadyLowestTerms: "{fraction} is already in lowest terms (GCD = 1)",
  stepGcd: "GCD({num}, {den}) = {g}",
} as const;

export default function FractionCalculatorWidget() {
  const [n1, setN1] = useState("1");
  const [d1, setD1] = useState("2");
  const [n2, setN2] = useState("1");
  const [d2, setD2] = useState("3");
  const [op, setOp] = useState<Op>("add");

  const strings = useToolUi(FRACTION_CALCULATOR_STRINGS);
  const formatNum = useToolNumber();

  const ops: { value: Op; label: string; symbol: string }[] = [
    { value: "add", label: strings.add, symbol: "+" },
    { value: "sub", label: strings.subtract, symbol: "−" },
    { value: "mul", label: strings.multiply, symbol: "×" },
    { value: "div", label: strings.divide, symbol: "÷" },
  ];

  const f1 = parseFraction(n1, d1);
  const f2 = parseFraction(n2, d2);
  const error = validationMessage(f1, f2, op, strings);
  const opSymbol = ops.find((o) => o.value === op)?.symbol ?? "+";

  let worked: Worked | null = null;
  let simplified: Worked | null = null;
  if (f1 !== null && f2 !== null && error === null) {
    worked = compute(f1, f2, op, strings);
    simplified = simplify({ num: worked.num, den: worked.den }, strings);
  }

  return (
    <section className="space-y-4 rounded-lg border border-border bg-muted/30 p-6">
      <h2 className="font-semibold text-foreground text-lg">
        {strings.twoFractions}
      </h2>
      <div className="flex flex-wrap items-center gap-6">
        <FractionField
          den={d1}
          idPrefix="frac-1"
          label={strings.firstFraction}
          num={n1}
          onDen={setD1}
          onNum={setN1}
          strings={strings}
        />
        <div className="space-y-2">
          <p className="font-medium text-foreground text-sm">
            {strings.operation}
          </p>
          <div className="flex flex-wrap gap-2">
            {ops.map((o) => (
              <label
                className="flex cursor-pointer items-center gap-2 rounded-lg border border-border bg-card px-3 py-2 text-sm transition-colors has-[:checked]:border-primary has-[:checked]:bg-primary/5"
                key={o.value}
              >
                <input
                  checked={op === o.value}
                  className="accent-primary"
                  name="fraction-op"
                  onChange={() => setOp(o.value)}
                  type="radio"
                  value={o.value}
                />
                {o.symbol} {o.label}
              </label>
            ))}
          </div>
        </div>
        <FractionField
          den={d2}
          idPrefix="frac-2"
          label={strings.secondFraction}
          num={n2}
          onDen={setD2}
          onNum={setN2}
          strings={strings}
        />
      </div>

      {error !== null && (
        <p className="text-muted-foreground text-sm">{error}</p>
      )}

      {worked !== null && simplified !== null && f1 !== null && f2 !== null && (
        <div className="space-y-2">
          <p className="font-semibold text-2xl text-foreground">
            {fracStr(f1)} {opSymbol} {fracStr(f2)} ={" "}
            {fracStr({ num: simplified.num, den: simplified.den })}
          </p>
          <div className="space-y-1 rounded-lg border border-border bg-card p-3 font-mono text-muted-foreground text-sm leading-relaxed">
            {worked.steps.map((line) => (
              <p key={line}>{line}</p>
            ))}
            {simplified.steps.map((line) => (
              <p key={line}>{line}</p>
            ))}
          </div>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <div className="rounded-lg border border-border bg-card p-4 text-center">
              <p className="font-semibold text-foreground text-xl">
                {fracStr({ num: simplified.num, den: simplified.den })}
              </p>
              <p className="mt-1 text-muted-foreground text-sm">
                {strings.improperFraction}
              </p>
            </div>
            <div className="rounded-lg border border-border bg-card p-4 text-center">
              <p className="font-semibold text-foreground text-xl">
                {mixedString({
                  num: simplified.num,
                  den: simplified.den,
                })}
              </p>
              <p className="mt-1 text-muted-foreground text-sm">
                {strings.mixedNumber}
              </p>
            </div>
            <div className="rounded-lg border border-border bg-card p-4 text-center">
              <p className="font-semibold text-foreground text-xl">
                {decimalString(
                  {
                    num: simplified.num,
                    den: simplified.den,
                  },
                  formatNum
                )}
              </p>
              <p className="mt-1 text-muted-foreground text-sm">
                {strings.decimal}
              </p>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
